import React, { useState } from 'react';

import { FormControl, InputLabel, Box, Button, Grid } from '@mui/material';
import { Theme } from '@mui/material/styles';

import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';

import ImageDialog from 'components/ImageDialog';

import { useTranslation } from 'services/localization/useTranslation';
import classNames from 'classnames';

interface Props {
	value: string | string[] | undefined;
	onChange: (url: string | string[] | undefined) => void;
	label: string;
	shopId: string | null;
	disabled?: boolean;
	isMultiImage?: boolean;
}

const ImageUpload = (props: Props) => {
	const { value, onChange, label, shopId, isMultiImage } = props;

	const { t } = useTranslation();
	const classes = useStyles();
	const [dialogOpen, setDialogOpen] = useState(false);

	const currentValues = !value ? undefined : Array.isArray(value) ? value : [value];

	return (
		<FormControl margin="normal" fullWidth>
			<Box mb={3}>
				<InputLabel shrink disabled={props.disabled}>
					{label}
				</InputLabel>
			</Box>
			{currentValues ? (
				<Grid container>
					{currentValues.map((src, idx) => (
						<Grid key={idx} item xs={idx === 0 ? 12 : 4} p={0.5}>
							<Box className={classes.imageWrapper}>
								<img
									key={idx}
									className={classNames(classes.image, {
										[classes.imageDisabled]: props.disabled,
									})}
									src={src}
									alt={label}
								/>
							</Box>
						</Grid>
					))}
				</Grid>
			) : (
				<Box className={classes.addImageWrapper}>
					<Box className={classes.noImageContent}>
						<Button
							variant="contained"
							color="primary"
							onClick={() => setDialogOpen(true)}
							disabled={props.disabled}
						>
							{t('product.addImage')}
						</Button>
					</Box>
				</Box>
			)}
			{value && (
				<Grid container spacing={2} alignItems="flex-end">
					<Grid item xs={12}>
						<Box mt={1} />
					</Grid>
					<Grid item xs={6} md={12}>
						<Button
							fullWidth
							variant="outlined"
							color="primary"
							onClick={() => setDialogOpen(true)}
							disabled={props.disabled}
						>
							{t('common:actions.edit', 'Edit')}
						</Button>
					</Grid>
				</Grid>
			)}
			{dialogOpen && shopId !== null && (
				<ImageDialog
					isOpen={dialogOpen}
					onCancel={() => setDialogOpen(false)}
					onSave={(value: string | string[] | null) => {
						setDialogOpen(false);
						onChange(value ?? undefined);
					}}
					img={value || null}
					shopId={shopId}
					isMultiImage={isMultiImage}
				/>
			)}
		</FormControl>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		addImageWrapper: {
			width: '100%',
			paddingTop: '100%',
			position: 'relative',
		},
		imageWrapper: {
			paddingBottom: '100%',
			display: 'block',
			position: 'relative',
		},
		image: {
			position: 'absolute',
			left: 0,
			top: 0,
			height: '100%',
			width: '100%',
			objectFit: 'contain',
			border: `1px solid ${theme.palette.border.inputOutline}`,
		},
		imageDisabled: {
			filter: 'grayscale(100%)',
		},
		noImageContent: {
			position: 'absolute',
			top: 0,
			left: 0,
			width: '100%',
			height: '100%',
			display: 'flex',
			flexDirection: 'column',
			alignItems: 'center',
			justifyContent: 'center',
			border: `1px solid ${theme.palette.border.inputOutline}`,
			borderRadius: '2px',
			'&:hover': {},
		},
		imageOverlay: {
			position: 'absolute',
			zIndex: 5,
			top: 0,
			left: 0,
			width: '100%',
			height: '100%',
			background: 'rgba(0,0,0,0.6)',
			opacity: 0,
			display: 'flex',
			flexDirection: 'column',
			alignItems: 'stretch',
			transition: 'opacity 0.3s ease',
			'&:hover': {
				opacity: 1,
			},
			'&:focus-within': {
				opacity: 1,
			},
		},
		imageOverlaySection: {
			flex: 1,
			'&:hover': {
				background: 'rgba(0,0,0,0.2)',
			},
			'&:focus': {
				background: 'rgba(0,0,0,0.2)',
			},
		},
	}),
);

export default ImageUpload;
